import { internalData, loadAsyncData, overrideInternalData, setupInternalData, syncDataMissingMessage } from "@utils/vue-migration/common/gaContext/gaContextInternals";
import { getFlags } from "@utils/vue-migration/common/gaContext/gaContextFlags";
import { type GaContextAsp, type GaContextCustomer, type GaContextDataSync, type GaContextFlags, Language } from "@newgenerated/shared/schema";
import { assert } from "@utils/assertion";
import { createTranslationFunction, type TranslationFn } from "@utils/vue-migration/translationUtils";

/*
 * Data that gets loaded for every Vue instance.
 */
export const dataKeysSync = ["currentLanguage", "flags", "customer", "asp"] satisfies (keyof GaContextDataSync)[];

/*
 * Data that can be loaded async if needed.
 */
export type GaContextDataAsync = {
  [Property in Language]: Map<string, string>;
};

export const dataKeysAsync = [...Language.getValues()] satisfies (keyof GaContextDataAsync)[];

export type AsyncLoaders = {
  [Property in keyof GaContextDataAsync]: () => Promise<GaContextDataAsync[Property]>;
};
type AsyncLoadersSimplified = Omit<AsyncLoaders, Language> & { loadTranslations: (lang: Language) => Promise<Map<string, string>> };

/**
 * This needs to run before any Vue instances are created.
 * @param forceOverrideLanguage If set, it forces the context to override the current language. Use with care, added for SSG in Astro
 */
export async function setupGaContext(syncData: GaContextDataSync, asyncLoaders: AsyncLoadersSimplified | AsyncLoaders, forceOverrideLanguage: boolean = false): Promise<void> {
  if ("loadTranslations" in asyncLoaders) {
    const { loadTranslations } = asyncLoaders;
    setupInternalData(syncData, {
      en: () => loadTranslations("en"),
      de: () => loadTranslations("de"),
      es: () => loadTranslations("es"),
      ru: () => loadTranslations("ru"),
      zh: () => loadTranslations("zh"),
      pt: () => loadTranslations("pt"),
      fr: () => loadTranslations("fr"),
      ar: () => loadTranslations("ar"),
    });
  } else {
    setupInternalData(syncData, asyncLoaders);
  }

  if (forceOverrideLanguage) {
    overrideInternalData(syncData.currentLanguage);
  }

  // init TMS texts of current language
  await loadAsyncData(getCurrentLanguage());
}

/**
 * Gets a synchronous translation function for the specified language.
 * The translations must already be loaded via setupGaContext() or loadAsyncData().
 * @throws Error if translations are not available (not loaded or still loading)
 */
function getSyncTranslationFunction(lang: Language): TranslationFn {
  const contextStatus = internalData.async[lang];

  if (contextStatus.state === "AVAILABLE") {
    return createTranslationFunction(contextStatus.value);
  }

  // Not available in the async data store
  const state = contextStatus.state === "UNINITIALIZED" ? "MISSING" : contextStatus.state;
  throw new Error(`Translation data for language '${lang}' is not available. ` + `Ensure setupGaContext() has completed before calling useGaContext(). ` + `Current state: ${state}`);
}

/*
 * Usage of the context
 */

/**
 * Convention:
 * - every property is a function.
 * - async function names start with "load".
 */

export type GaContext = {
  /**
   * @deprecated Please use the translation function of the context the vue widget generator provides.
   */
  t: TranslationFn;
  currentLanguage: () => Language;
  flags: () => GaContextFlags;
  loadTranslation: (lang: Language) => Promise<TranslationFn>;
  customer: GaContextCustomer | null;
  asp: GaContextAsp | null;
};

export function useGaContext(): GaContext {
  const lang = getCurrentLanguage();

  // Translation function is synchronous because setupGaContext() pre-loads current language translations
  const t = getSyncTranslationFunction(lang);

  const currentLanguage = (): Language => getCurrentLanguage();

  const loadTranslation = async (lang: Language): Promise<TranslationFn> => {
    await loadAsyncData(lang);
    return getSyncTranslationFunction(lang);
  };

  assert(internalData.sync.customer.state === "AVAILABLE", "sync data is not initialized");
  assert(internalData.sync.asp.state === "AVAILABLE", "sync data is not initialized");
  return { t, currentLanguage, loadTranslation, flags: getFlags, customer: internalData.sync.customer.value, asp: internalData.sync.asp.value };
}

export function getCurrentLanguage(): Language {
  const currentLanguage = internalData.sync.currentLanguage;
  assert(currentLanguage.state === "AVAILABLE", syncDataMissingMessage("currentLanguage"));
  return currentLanguage.value;
}
